import type { BrandName } from '@/components/ui/BrandIcon';
import type { IconName } from '@/components/ui/Icon';
import type {
  IndustryId,
  PartnerSlug,
  ProjectSlug,
  ServiceId,
} from '@/i18n/types';

/**
 * Facts, not copy.
 *
 * Everything in this file is the same in all three languages: phone numbers,
 * coordinates, file paths, outbound URLs, icon choices and the order things
 * appear in. Anything a translator would want to touch lives in
 * `src/i18n/dictionaries/*` and is joined to this data by `src/data/resolve.ts`.
 *
 * The split is what keeps the three languages honest — a screenshot path or a
 * store URL cannot drift between them, because there is only one of it.
 */

export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ?? 'https://soft.mpmg.uz'
).replace(/\/$/, '');

export const company = {
  name: 'MPMG SOFT',
  legalName: 'MPMG SOFT MChJ',
  foundingYear: 2019,
  email: 'info@mpmgsoft.uz',
  salesEmail: 'sales@mpmgsoft.uz',
  /**
   * One number — sales, support and general enquiries. The header, footer, CTA
   * blocks and JSON-LD all read it from here, so changing it in one place is
   * enough. Its *label* is translated (`company.phoneLabel`).
   */
  phone: {
    value: '+998 95 959 56 59',
    href: 'tel:+998959595659',
  },
  address: {
    postalCode: '100011',
    country: 'UZ',
    geo: { lat: 41.3201468, lng: 69.2625445 },
  },
  hoursSchema: {
    days: [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ] as const,
    opens: '09:00',
    closes: '19:00',
  },
  /** ⚠️ Placeholder handles — havolalar almashtirilmaguncha ochilmaydi. */
  socials: [
    { brand: 'telegram', href: 'https://t.me/mpmgsoft' },
    { brand: 'linkedin', href: 'https://www.linkedin.com/company/mpmgsoft' },
    { brand: 'instagram', href: 'https://www.instagram.com/mpmgsoft' },
    { brand: 'youtube', href: 'https://www.youtube.com/@mpmgsoft' },
  ] satisfies readonly { brand: BrandName; href: string }[],
} as const;

/** Opens the office pin in the visitor's own map app. */
export const officeMapUrl = 'https://maps.app.goo.gl/iLpB3QkV3vuHrk5o9';

export const primaryPhone = company.phone;

/* ------------------------------------------------------------------ *
 * Services — id, icon and order. Titles and copy come from the dictionary.
 * ------------------------------------------------------------------ */

export const serviceIcons = {
  erp: 'grid',
  crm: 'users',
  ecommerce: 'cart',
  custom: 'blocks',
  integration: 'network',
  mobile: 'smartphone',
  bi: 'chart',
  audit: 'shield',
} satisfies Record<ServiceId, IconName>;

/** Card order on the home page and on /xizmatlar. */
export const serviceOrder: readonly ServiceId[] = [
  'erp',
  'crm',
  'ecommerce',
  'custom',
  'integration',
  'mobile',
  'bi',
  'audit',
];

/** The six services listed in the footer column. */
export const footerServiceOrder: readonly ServiceId[] = [
  'erp',
  'crm',
  'ecommerce',
  'custom',
  'integration',
  'mobile',
];

/* ------------------------------------------------------------------ *
 * Process — the station numbers. Titles and copy come from the dictionary.
 * ------------------------------------------------------------------ */

export const processNumbers = ['01', '02', '03', '04', '05'] as const;

/* ------------------------------------------------------------------ *
 * Projects
 * ------------------------------------------------------------------ */

export type ProjectFacts = {
  slug: ProjectSlug;
  /** Product / brand name — the strongest identifier in the card, untranslated. */
  name: string;
  year: string;
  image: string;
  /**
   * CSS `object-position` for the cover crop. The screenshots arrive in very
   * different aspect ratios (portrait phone stacks vs. 2:1 desktop grabs), so
   * a per-project focal point keeps the brand visible after cropping.
   */
  focal?: string;
  /**
   * Public destinations for the product — site first, then stores. Omit the
   * field entirely when nothing is published; never point at a placeholder.
   */
  links?: readonly { label: string; href: string }[];
};

/**
 * Portfolio.
 *
 * Every entry is a shipped product with a public presence; the figures in the
 * dictionary come from the product's own site or store listing, not from
 * estimates. Projects without a public URL simply render without the visit
 * action — do not invent one.
 */
export const projectFacts: readonly ProjectFacts[] = [
  {
    slug: 'uzchess',
    name: 'UzChess',
    year: '2024',
    image: '/projects/UzChess.png',
    links: [
      { label: 'uzchesss.uz', href: 'https://uzchesss.uz/' },
      {
        label: 'App Store',
        href: 'https://apps.apple.com/us/app/uzchess-shaxmat-akademiya/id6502834977',
      },
      {
        label: 'Google Play',
        href: 'https://play.google.com/store/apps/details?id=uz.uzchess.akademiya',
      },
    ],
  },
  {
    slug: 'doclab',
    name: 'Doclab',
    year: '2026',
    image: '/projects/Doclab.jpg',
    links: [{ label: 'doclab.uz', href: 'https://doclab.uz/' }],
  },
  {
    slug: 'tarantool-games',
    name: 'TaranTool Games',
    year: '2026',
    image: '/projects/TarantoolGames.jpg',
    links: [
      { label: 'tarantool.games', href: 'https://tarantool.games/' },
      {
        label: 'Steam',
        href: 'https://store.steampowered.com/app/4735890/Power_of_Desert_Quvvat_ulSahro/',
      },
    ],
  },
  {
    slug: 'uzcombinator',
    name: 'UzCombinator',
    year: '2025',
    image: '/projects/UzCombinator.png',
    links: [{ label: 'uzcombinator.uz', href: 'https://uzcombinator.uz/' }],
  },
  {
    slug: 'sherdor-games',
    name: 'Sherdor Games',
    year: '2026',
    image: '/projects/SherdorGames.jpg',
    links: [{ label: 'sherdorgames.uz', href: 'https://sherdorgames.uz/' }],
  },
  {
    slug: 'castinggo',
    name: 'CastingGo',
    year: '2026',
    image: '/projects/CastingGo.png',
    links: [
      { label: 'castinggo.uz', href: 'https://castinggo.uz/' },
      {
        label: 'App Store',
        href: 'https://apps.apple.com/us/app/castinggo/id6756616108',
      },
      {
        label: 'Google Play',
        href: 'https://play.google.com/store/apps/details?id=uz.castinggo.app',
      },
    ],
  },
  {
    slug: '916tv',
    name: '916TV',
    year: '2026',
    image: '/projects/916TV.png',
    focal: 'center 52%',
    links: [
      {
        label: 'App Store',
        href: 'https://apps.apple.com/us/app/tv916-movies/id6768674034',
      },
    ],
  },
  {
    slug: 'maqsad',
    name: 'Maqsad',
    year: '2026',
    image: '/projects/Maqsad.png',
    links: [
      {
        label: 'App Store',
        href: 'https://apps.apple.com/us/app/maqsad-talantlar/id6788289255',
      },
    ],
  },
  {
    slug: 'trainball',
    name: 'TrainBall',
    year: '2025',
    image: '/projects/Trainball.png',
    links: [
      {
        label: 'App Store',
        href: 'https://apps.apple.com/us/app/trainball/id6747034373',
      },
      {
        label: 'Google Play',
        href: 'https://play.google.com/store/apps/details?id=uz.uzfootball.akademiya',
      },
    ],
  },
  {
    slug: 'watchz',
    name: 'Watchz',
    year: '2026',
    image: '/projects/Watchz.jpg',
    links: [{ label: 'watchz.tech', href: 'https://watchz.tech/' }],
  },
];

/* ------------------------------------------------------------------ *
 * Industries — id, icon and order.
 * ------------------------------------------------------------------ */

export const industryIcons = {
  manufacturing: 'factory',
  logistics: 'truck',
  retail: 'store',
  health: 'health',
  construction: 'building',
  education: 'education',
  distribution: 'package',
  technology: 'cpu',
} satisfies Record<IndustryId, IconName>;

export const industryOrder: readonly IndustryId[] = [
  'manufacturing',
  'logistics',
  'retail',
  'health',
  'construction',
  'education',
  'distribution',
  'technology',
];

/* ------------------------------------------------------------------ *
 * Partners
 * ------------------------------------------------------------------ */

export type PartnerFacts = {
  slug: PartnerSlug;
  /** Organisation name as it writes itself — never translated. */
  name: string;
  href: string;
  logo: string;
};

export const partnerFacts: readonly PartnerFacts[] = [
  {
    slug: 'uzcombinator',
    name: 'UzCombinator',
    href: 'https://uzcombinator.uz/',
    logo: '/images/partners/uzcombinator.png',
  },
  {
    slug: 'yoshlar-agentligi',
    name: 'Yoshlar Ishlari Agentligi',
    href: 'https://gov.uz/oz/yoshlar',
    logo: '/images/partners/yoshlar-agentligi.png',
  },
  {
    slug: 'uzchess',
    name: 'UzChess',
    href: 'https://uzchesss.uz/',
    logo: '/images/partners/uzchess.png',
  },
  {
    slug: 'milliy-karaoke',
    name: 'Milliy Karaoke',
    href: 'https://www.youtube.com/channel/UCRR37yX_y353K_05tIQfp9g',
    logo: '/images/partners/milliy-karaoke.png',
  },
  {
    slug: 'tarantool-games',
    name: 'TaranTool Games',
    href: 'https://tarantool.games/',
    logo: '/images/partners/tarantool-games.png',
  },
  {
    slug: 'sherdor-games',
    name: 'Sherdor Games',
    href: 'https://sherdorgames.uz/',
    logo: '/images/partners/sherdor-games.png',
  },
];
